/**
 * LottiePicker Component
 * A carousel-style picker for selecting Lottie animations
 */

import React, { useState, useRef, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Modal,
  FlatList,
  Dimensions,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import LottieView from "lottie-react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import {
  getAllLotties,
  getLottiesByCategory,
  getLottieByKey,
  getAllCategories,
  LottieItem,
  LottieCategory,
  LOTTIE_CATEGORY_LABELS,
  LOTTIE_CATEGORY_COLORS,
} from "@/lib/lottie-registry";

const { width: SCREEN_WIDTH } = Dimensions.get("window");
const ITEM_SIZE = 100;
const ITEMS_PER_ROW = 3;

interface LottiePickerProps {
  /** Currently selected Lottie key */
  value: string | null;
  /** Callback when a Lottie is selected */
  onChange: (key: string) => void;
  /** Optional label */
  label?: string;
  /** Optional placeholder text */
  placeholder?: string;
}

/**
 * LottiePicker component
 */
export const LottiePicker: React.FC<LottiePickerProps> = ({
  value,
  onChange,
  label = "Animatie",
  placeholder = "Kies een animatie",
}) => {
  const colors = useThemeColors();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<LottieCategory | "all">("all");
  
  const selectedLottie = value ? getLottieByKey(value) : null;
  const categories = getAllCategories();
  
  /**
   * Get filtered Lotties based on category
   */
  const filteredLotties = selectedCategory === "all" 
    ? getAllLotties() 
    : getLottiesByCategory(selectedCategory);

  /**
   * Handle Lottie selection
   */
  const handleSelect = useCallback((item: LottieItem) => {
    onChange(item.key);
    setModalVisible(false);
  }, [onChange]);

  /**
   * Render a single Lottie item in the grid
   */
  const renderLottieItem = ({ item }: { item: LottieItem }) => {
    const isSelected = value === item.key;
    
    return (
      <TouchableOpacity
        onPress={() => handleSelect(item)}
        className="items-center justify-center p-2"
        style={{
          width: (SCREEN_WIDTH - 48) / ITEMS_PER_ROW,
          height: ITEM_SIZE + 30,
        }}
        accessibilityRole="button"
        accessibilityLabel={item.name}
      >
        <View
          className="rounded-xl items-center justify-center overflow-hidden"
          style={{
            width: ITEM_SIZE - 10,
            height: ITEM_SIZE - 10,
            backgroundColor: isSelected ? `${colors.primary}20` : colors.surfaceSecondary,
            borderWidth: isSelected ? 2 : 0,
            borderColor: colors.primary,
          }}
        >
          <LottieView
            source={item.source}
            style={{ width: ITEM_SIZE - 30, height: ITEM_SIZE - 30 }}
            autoPlay
            loop
          />
        </View>
        <Text
          className="text-xs mt-1 text-center"
          style={{ color: isSelected ? colors.primary : colors.textSecondary }}
          numberOfLines={1}
        >
          {item.name}
        </Text>
      </TouchableOpacity>
    );
  };

  return (
    <>
      {/* Trigger Button */}
      <View>
        {label && (
          <Text
            className="text-sm font-medium mb-2"
            style={{ color: colors.textSecondary }}
          >
            {label}
          </Text>
        )}
        <TouchableOpacity
          onPress={() => setModalVisible(true)}
          className="rounded-xl p-4 flex-row items-center"
          style={{
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
          }}
          accessibilityRole="button"
          accessibilityLabel="Kies animatie"
        >
          {selectedLottie ? (
            <>
              <View
                className="w-12 h-12 rounded-lg items-center justify-center mr-3"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <LottieView
                  source={selectedLottie.source}
                  style={{ width: 40, height: 40 }}
                  autoPlay
                  loop
                />
              </View>
              <View className="flex-1">
                <Text className="font-medium" style={{ color: colors.text }}>
                  {selectedLottie.name}
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  {LOTTIE_CATEGORY_LABELS[selectedLottie.category]}
                </Text>
              </View>
            </>
          ) : (
            <>
              <View
                className="w-12 h-12 rounded-lg items-center justify-center mr-3"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="sparkles" size={24} color={colors.textTertiary} />
              </View>
              <Text className="flex-1" style={{ color: colors.textTertiary }}>
                {placeholder}
              </Text>
            </>
          )}
          <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
        </TouchableOpacity>
      </View>

      {/* Picker Modal */}
      <Modal
        visible={modalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setModalVisible(false)}
      >
        <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
          {/* Header */}
          <View
            className="px-4 py-4 flex-row items-center justify-between border-b"
            style={{ borderColor: colors.border }}
          >
            <TouchableOpacity
              onPress={() => setModalVisible(false)}
              className="w-10 h-10 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface }}
            >
              <Ionicons name="close" size={22} color={colors.text} />
            </TouchableOpacity>
            <Text className="font-bold text-lg" style={{ color: colors.text }}>
              Kies Animatie
            </Text>
            <View className="w-10" />
          </View>

          {/* Category Tabs */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            className="py-3 px-4"
            style={{ maxHeight: 56 }}
          >
            <TouchableOpacity
              onPress={() => setSelectedCategory("all")}
              className="px-4 py-2 rounded-full mr-2"
              style={{
                backgroundColor: selectedCategory === "all" 
                  ? colors.primary 
                  : colors.surfaceSecondary,
              }}
            >
              <Text
                className="font-medium"
                style={{
                  color: selectedCategory === "all" ? "#FFFFFF" : colors.textSecondary,
                }}
              >
                Alles ({getAllLotties().length})
              </Text>
            </TouchableOpacity>

            {categories.map((category) => (
              <TouchableOpacity
                key={category}
                onPress={() => setSelectedCategory(category)}
                className="px-4 py-2 rounded-full mr-2"
                style={{
                  backgroundColor: selectedCategory === category
                    ? LOTTIE_CATEGORY_COLORS[category]
                    : colors.surfaceSecondary,
                }}
              >
                <Text
                  className="font-medium"
                  style={{
                    color: selectedCategory === category ? "#FFFFFF" : colors.textSecondary,
                  }}
                >
                  {LOTTIE_CATEGORY_LABELS[category]}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          {/* Lottie Grid */}
          <FlatList
            data={filteredLotties}
            renderItem={renderLottieItem}
            keyExtractor={(item) => item.key}
            numColumns={ITEMS_PER_ROW}
            contentContainerStyle={{ padding: 16 }}
            showsVerticalScrollIndicator={false}
          />
        </SafeAreaView>
      </Modal>
    </>
  );
};

export default LottiePicker;

